import { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, serverTimestamp, updateDoc } from 'firebase/firestore';
import { ChevronDown, Download, DollarSign, TrendingUp, Plane, Users } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { DEMO_AIRCRAFT, DEMO_FLIGHTS, DEMO_TRANSACTIONS, DEMO_LESSEES } from '../../lib/demoData';
import { fmtHours, fmtUSD } from '../../lib/billingCycles';
import { PageHeader } from '../../components/PageHeader';
import { TrendChart } from '../../components/TrendChart';
import type { AircraftDoc, FlightDoc, TransactionDoc, UserDoc } from '../../lib/types';

const DAY = 24 * 60 * 60 * 1000;

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function AdminRevenue() {
  const { demoMode } = useAuth();
  const [transactions, setTransactions] = useState<TransactionDoc[]>([]);
  const [flights, setFlights] = useState<FlightDoc[]>([]);
  const [aircraft, setAircraft] = useState<AircraftDoc[]>([]);
  const [users, setUsers] = useState<UserDoc[]>([]);
  // Which pilot's flight-by-flight breakdown is open under "By lessee".
  const [openLessee, setOpenLessee] = useState<string | null>(null);

  useEffect(() => {
    if (demoMode) {
      setTransactions(DEMO_TRANSACTIONS);
      setFlights(DEMO_FLIGHTS);
      setAircraft(DEMO_AIRCRAFT);
      setUsers(DEMO_LESSEES);
      return;
    }
    if (!db) return;
    const subs = [
      onSnapshot(collection(db, 'transactions'), (s) => setTransactions(s.docs.map((d) => d.data() as TransactionDoc))),
      onSnapshot(collection(db, 'flights'), (s) => setFlights(s.docs.map((d) => d.data() as FlightDoc))),
      onSnapshot(collection(db, 'aircraft'), (s) => setAircraft(s.docs.map((d) => d.data() as AircraftDoc))),
      onSnapshot(collection(db, 'users'), (s) => setUsers(s.docs.map((d) => d.data() as UserDoc))),
    ];
    return () => subs.forEach((u) => u());
  }, [demoMode]);

  const now = new Date();
  const today0 = startOfDay(now).getTime();
  const week0 = today0 - 6 * DAY;
  const month0 = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const quarter0 = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1).getTime();
  const year0 = new Date(now.getFullYear(), 0, 1).getTime();

  const nameByUid = (uid: string) => {
    const u = users.find((x) => (x.uid || '') === uid);
    return u ? `${u.personal?.firstName ?? ''} ${u.personal?.lastName ?? ''}`.trim() || u.email || uid : uid;
  };
  const txDate = (t: TransactionDoc) => t.createdAt?.toDate?.() ?? new Date(0);

  // Flights the pilot elected to settle in person (Casey, 2026-08-28). Open
  // receivables — deliberately NOT counted as revenue until the money arrives.
  const cashDue = transactions.filter((t) => t.status === 'cash_due');
  const markCashPaid = async (t: TransactionDoc) => {
    if (!confirm(`Mark ${fmtUSD(t.amount)} from ${nameByUid(t.lesseeUid)} as received in cash?`)) return;
    if (demoMode || !db) {
      setTransactions((cur) =>
        cur.map((x) => (x.transactionId === t.transactionId ? { ...x, status: 'succeeded' as const } : x))
      );
      return;
    }
    // The server (onTransactionUpdated) sees cash_due → succeeded and emails the
    // pilot a cash receipt. paymentMethod is for the pilot's Payments list.
    await updateDoc(doc(db, 'transactions', t.transactionId), {
      status: 'succeeded',
      paymentMethod: 'cash',
      cashReceivedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  };

  // Revenue = succeeded charges only. Grounding credits + refunds are money going
  // back OUT, so they never count toward revenue.
  const tx = useMemo(
    () => transactions.filter((t) => t.status === 'succeeded' && t.type !== 'grounding_credit' && t.type !== 'refund'),
    [transactions]
  );

  const sum = (fromMs: number) => tx.filter((t) => txDate(t).getTime() >= fromMs).reduce((s, t) => s + t.amount, 0);
  const today = sum(today0);
  const week = sum(week0);
  const month = sum(month0);
  const quarter = sum(quarter0);
  const ytd = sum(year0);
  const lifetime = tx.reduce((s, t) => s + t.amount, 0);

  // 12-month trend
  const months = useMemo(() => {
    const arr: { label: string; value: number }[] = [];
    for (let i = 11; i >= 0; i--) {
      const m = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const me = new Date(m.getFullYear(), m.getMonth() + 1, 1);
      const v = tx
        .filter((t) => txDate(t) >= m && txDate(t) < me)
        .reduce((s, t) => s + t.amount, 0);
      arr.push({ label: m.toLocaleString([], { month: 'short' }), value: v });
    }
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tx]);

  // Per-lessee breakdown
  const byLessee = useMemo(() => {
    const map = new Map<string, { uid: string; name: string; ytd: number; lifetime: number; hours: number }>();
    for (const t of tx) {
      const e = map.get(t.lesseeUid) ?? { uid: t.lesseeUid, name: nameByUid(t.lesseeUid), ytd: 0, lifetime: 0, hours: 0 };
      e.lifetime += t.amount;
      if (txDate(t).getTime() >= year0) e.ytd += t.amount;
      map.set(t.lesseeUid, e);
    }
    for (const f of flights) {
      const e = map.get(f.lesseeUid);
      if (e) e.hours += f.hoursFlown || 0;
    }
    return Array.from(map.values()).sort((a, b) => b.lifetime - a.lifetime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tx, flights, users, year0]);

  function exportCsv() {
    const rows = [
      ['Transaction ID', 'Date', 'Lessee', 'Aircraft', 'Type', 'Amount', 'Status', 'Stripe ID'].join(','),
      ...transactions.map((t) => {
        return [
          t.transactionId,
          txDate(t).toISOString(),
          `"${nameByUid(t.lesseeUid)}"`,
          t.aircraftId,
          t.type,
          t.amount.toFixed(2),
          t.status,
          t.stripeChargeId ?? '',
        ].join(',');
      }),
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ajm-transactions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <PageHeader
        title="Revenue"
        subtitle="Live snapshot, trends, breakdowns, and exports."
        actions={
          <button onClick={exportCsv} className="btn-secondary text-sm flex items-center gap-1.5">
            <Download size={14} /> Export transactions (CSV)
          </button>
        }
      />

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Tile icon={<DollarSign size={14} />} label="Today" value={fmtUSD(today)} />
        <Tile icon={<DollarSign size={14} />} label="This week" value={fmtUSD(week)} />
        <Tile icon={<DollarSign size={14} />} label="This month" value={fmtUSD(month)} />
        <Tile icon={<DollarSign size={14} />} label="This quarter" value={fmtUSD(quarter)} />
        <Tile icon={<TrendingUp size={14} />} label="Year-to-date" value={fmtUSD(ytd)} accent />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 card p-5">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-lg font-semibold text-navy-900">12-month trend</h2>
            <div className="text-xs text-ink-muted">Lifetime {fmtUSD(lifetime)}</div>
          </div>
          <TrendChart points={months} format={fmtUSD} unitLabel="/mo" />
        </div>

        <div className="card p-5">
          <h2 className="font-display text-lg font-semibold text-navy-900 mb-3">Outstanding</h2>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center justify-between">
              <span className="text-ink">Cash owed</span>
              <span className="font-semibold text-amber-800">
                {fmtUSD(cashDue.reduce((acc, t) => acc + (t.amount || 0), 0))}
              </span>
            </li>
            {cashDue.map((t) => (
              <li key={t.transactionId} className="flex items-center justify-between gap-2 pl-3 text-xs">
                <span className="min-w-0 flex-1 truncate text-ink-muted">
                  {nameByUid(t.lesseeUid)} &middot; {fmtUSD(t.amount)}
                </span>
                <button
                  type="button"
                  className="btn-secondary shrink-0 px-2 py-0.5 text-[11px]"
                  onClick={() => markCashPaid(t)}
                >
                  Mark paid
                </button>
              </li>
            ))}
            <li className="flex items-center justify-between">
              <span className="text-ink">Failed charges</span>
              <span className="badge-success">
                {transactions.filter((t) => t.status === 'failed').length}
              </span>
            </li>
            <li className="flex items-center justify-between">
              <span className="text-ink">Disputed</span>
              <span className="badge-success">
                {transactions.filter((t) => t.status === 'disputed').length}
              </span>
            </li>
          </ul>
          <p className="mt-3 text-xs text-ink-muted">
            Charges run automatically per flight and on each lessee’s monthly anchor.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-navy-900 flex items-center gap-2">
              <Users size={16} /> By lessee
            </h2>
          </div>
          {byLessee.length === 0 ? (
            <p className="px-5 py-6 text-sm text-ink-muted">No revenue yet.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {byLessee.map((row) => {
                const open = openLessee === row.uid;
                return (
                  <div key={row.uid}>
                    <button
                      type="button"
                      onClick={() => setOpenLessee(open ? null : row.uid)}
                      aria-expanded={open}
                      className="w-full px-5 py-3 text-left hover:bg-sky-50/50"
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                        <span className="font-medium text-navy-900">{row.name}</span>
                        <span className="flex items-center gap-2 font-display font-semibold text-navy-900">
                          {fmtUSD(row.lifetime)}
                          <ChevronDown size={14} className={`text-ink-muted transition-transform ${open ? 'rotate-180' : ''}`} />
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-ink-muted">
                        <span>{fmtHours(row.hours)} flown</span>
                        <span>YTD {fmtUSD(row.ytd)}</span>
                        <span className="text-navy-700">{open ? 'Hide flights' : 'See each flight'}</span>
                      </div>
                    </button>
                    {open && (
                      <LesseeBreakdown
                        flights={flights.filter((f) => f.lesseeUid === row.uid)}
                        tx={tx.filter((t) => t.lesseeUid === row.uid)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="card p-5">
          <h2 className="font-display text-lg font-semibold text-navy-900 flex items-center gap-2 mb-3">
            <Plane size={16} /> By aircraft
          </h2>
          {aircraft.map((a) => {
            const aHours = flights
              .filter((f) => f.aircraftId === a.aircraftId)
              .reduce((s, f) => s + (f.hoursFlown || 0), 0);
            const aRev = tx.filter((t) => t.aircraftId === a.aircraftId).reduce((s, t) => s + t.amount, 0);
            return (
              <div key={a.aircraftId} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-none">
                <div>
                  <div className="font-medium text-navy-900">{a.tailNumber}</div>
                  <div className="text-xs text-ink-muted">
                    {fmtHours(aHours)} flown lifetime · ${a.hourlyRate}/hr
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display font-semibold text-navy-900">{fmtUSD(aRev)}</div>
                  <div className="text-xs text-ink-muted">lifetime</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function Tile({
  icon,
  label,
  value,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className={`card p-4 ${accent ? 'bg-brand text-white' : ''}`}>
      <div className={`text-xs uppercase tracking-wider flex items-center gap-1 ${accent ? 'text-sky-100' : 'text-ink-muted'}`}>
        {icon} {label}
      </div>
      <div className={`mt-2 text-xl font-display font-semibold ${accent ? 'text-white' : 'text-navy-900'}`}>
        {value}
      </div>
    </div>
  );
}

// Tap a pilot in "By lessee" and this opens under them: every flight they
// flew, what each one brought in, and the charges that weren't a flight
// (minimums, fuel, custom) — adding up to the figure on the row.
function LesseeBreakdown({ flights, tx }: { flights: FlightDoc[]; tx: TransactionDoc[] }) {
  const byFlight = new Map<string, number>();
  for (const t of tx) if (t.flightId) byFlight.set(t.flightId, (byFlight.get(t.flightId) ?? 0) + t.amount);
  const sorted = flights.slice().sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  const flightIds = new Set(flights.map((f) => f.flightId));
  const other = tx.filter((t) => !t.flightId || !flightIds.has(t.flightId));
  const flightTotal = sorted.reduce((s, f) => s + (byFlight.get(f.flightId) ?? 0), 0);
  const otherTotal = other.reduce((s, t) => s + t.amount, 0);
  const route = (f: FlightDoc) => {
    const legs = [f.departureAirport, ...(f.destinations ?? [])].filter(Boolean);
    const away = legs.filter((a, i) => i === 0 || a !== legs[i - 1]);
    return away.length <= 1 ? `Local · ${f.departureAirport}` : away.join(' → ');
  };
  const when = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return (
    <div className="border-t border-slate-100 bg-slate-50/60 px-5 py-3 text-sm">
      {sorted.length === 0 ? (
        <p className="text-ink-muted">No flights logged.</p>
      ) : (
        <table className="w-full">
          <thead className="text-[11px] uppercase tracking-wider text-ink-muted">
            <tr>
              <th className="py-1 text-left font-medium">Flight</th>
              <th className="py-1 text-right font-medium">Hrs</th>
              <th className="py-1 text-right font-medium">Made</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200/70">
            {sorted.map((f) => {
              const made = byFlight.get(f.flightId);
              return (
                <tr key={f.flightId}>
                  <td className="py-1.5 pr-2">
                    <div className="font-medium text-navy-900">{f.date} · {f.aircraftId}</div>
                    <div className="text-xs text-ink-muted">{route(f)}</div>
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-ink-muted">{(f.hoursFlown || 0).toFixed(1)}</td>
                  <td className="py-1.5 text-right font-medium tabular-nums text-navy-900">
                    {made != null ? fmtUSD(made) : <span className="text-ink-muted" title="No charge tied to this flight — covered by the monthly minimum, or not billed">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="text-xs text-ink-muted">
              <td className="pt-2" colSpan={2}>{sorted.length} {sorted.length === 1 ? 'flight' : 'flights'} · {fmtHours(sorted.reduce((s, f) => s + (f.hoursFlown || 0), 0))}</td>
              <td className="pt-2 text-right tabular-nums">{fmtUSD(flightTotal)}</td>
            </tr>
          </tfoot>
        </table>
      )}
      {other.length > 0 && (
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-wider text-ink-muted">Not a flight</div>
          <div className="divide-y divide-slate-200/70">
            {other
              .slice()
              .sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0))
              .map((t) => (
                <div key={t.transactionId} className="flex items-baseline justify-between gap-3 py-1.5">
                  <span className="min-w-0 truncate text-ink">
                    {t.description || t.type.replace(/_/g, ' ')}
                    <span className="ml-2 text-xs text-ink-muted">{t.createdAt?.toDate ? when(t.createdAt.toDate()) : ''}</span>
                  </span>
                  <span className="shrink-0 font-medium tabular-nums text-navy-900">{fmtUSD(t.amount)}</span>
                </div>
              ))}
          </div>
          <div className="pt-2 text-right text-xs tabular-nums text-ink-muted">{fmtUSD(otherTotal)}</div>
        </div>
      )}
    </div>
  );
}
